import React, { memo, useEffect, useState } from 'react';
import { Box, ButtonBase, Stack, TextField, Tooltip, Typography, alpha, useTheme } from '@mui/material';
import { Check } from '@mui/icons-material';
import { PALETTES } from '../catalog';

type Colors = { primary: string; accent: string };

interface Props {
  value: Colors;
  onChange: (colors: Colors) => void;
}

const HEX = /^#[0-9a-f]{6}$/i;
const same = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();

/** One colour: a swatch that opens the system picker, and its hex code. */
const ColorField: React.FC<{ label: string; value: string; onChange: (hex: string) => void }> = ({ label, value, onChange }) => {
  const [text, setText] = useState(value);
  useEffect(() => { setText(value); }, [value]);
  const commit = (next: string) => {
    const hex = next.startsWith('#') ? next : `#${next}`;
    if (HEX.test(hex)) onChange(hex.toUpperCase());
  };
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
      <Box
        component="label"
        sx={{
          position: 'relative', width: 36, height: 36, flexShrink: 0, borderRadius: 1.5, bgcolor: value, cursor: 'pointer',
          boxShadow: t => `inset 0 0 0 1px ${alpha(t.palette.common.black, 0.12)}`,
        }}
      >
        <Box
          component="input"
          type="color"
          value={value}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value.toUpperCase())}
          aria-label={`${label} colour`}
          sx={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', width: '100%', height: '100%', border: 0, p: 0 }}
        />
      </Box>
      <TextField
        size="small"
        label={label}
        value={text}
        onChange={e => { setText(e.target.value); commit(e.target.value.trim()); }}
        onBlur={() => setText(value)}
        error={!HEX.test(text.startsWith('#') ? text : `#${text}`)}
        inputProps={{ maxLength: 7, spellCheck: false, style: { fontFamily: 'monospace', textTransform: 'uppercase' } }}
        sx={{ minWidth: 0 }}
      />
    </Stack>
  );
};

/** Curated colour schemes, or any two colours of the school's own. */
const PalettePicker: React.FC<Props> = ({ value, onChange }) => {
  const theme = useTheme();
  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 1 }}>
        {PALETTES.map(p => {
          const selected = same(p.primary, value.primary) && same(p.accent, value.accent);
          return (
            <Tooltip key={p.name} title={p.name}>
              <ButtonBase
                onClick={() => onChange({ primary: p.primary, accent: p.accent })}
                aria-label={p.name}
                aria-pressed={selected}
                sx={{
                  aspectRatio: '1', borderRadius: '50%', overflow: 'hidden', position: 'relative',
                  background: `linear-gradient(135deg, ${p.primary} 0 58%, ${p.accent} 58% 100%)`,
                  boxShadow: selected
                    ? `0 0 0 2px ${theme.palette.background.paper}, 0 0 0 4px ${theme.palette.primary.main}`
                    : `inset 0 0 0 1px ${alpha(theme.palette.common.black, 0.1)}`,
                  transition: 'transform .12s',
                  '&:hover': { transform: 'scale(1.06)' },
                }}
              >
                {selected && <Check sx={{ color: '#fff', fontSize: 16, filter: 'drop-shadow(0 1px 1px rgba(0,0,0,.4))' }} />}
              </ButtonBase>
            </Tooltip>
          );
        })}
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Or the school's own colours</Typography>
        <Stack direction="row" spacing={1.5}>
          <ColorField label="Main" value={value.primary} onChange={primary => onChange({ ...value, primary })} />
          <ColorField label="Accent" value={value.accent} onChange={accent => onChange({ ...value, accent })} />
        </Stack>
      </Box>
    </Stack>
  );
};

export default memo(PalettePicker);
